import React, { useState, useEffect } from 'react';
import { Heart, Moon, UserCheck, GraduationCap, Shirt, Wind, Activity, Footprints, ShoppingCart, Receipt, Star, X, CheckCircle2, CreditCard, Loader2, ExternalLink, Package } from 'lucide-react';
import { COLORS, STORE_CATEGORIES } from '../data/mockData';
import { fetchProducts, fetchCart, addToCart as apiAddToCart, removeFromCart, checkoutCart, createPaymentCheckout } from '../services/api';

const ICON_MAP = { Heart, Moon, UserCheck, GraduationCap, Shirt, Wind, Activity, Footprints, Package };
const CAT_COLORS = { 'health-devices': '#2196F3', 'consultations': '#5C6BC0', 'seminars': '#FF9800', 'clothing': '#00BCD4', 'environmental': '#4CAF50' };
const catColor = (cat) => CAT_COLORS[cat] || COLORS.primary;
const fmtPrice = (p) => (p == null ? null : `$${Number(p).toFixed(2)}`);

function AffiliateBadge({ partner }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, letterSpacing: 0.5, backgroundColor: '#FF990018', color: '#B36B00', textTransform: 'uppercase' }}>
      <ExternalLink size={10} /> {partner === 'amazon' ? 'Amazon Partner' : 'Partner'}
    </span>
  );
}

export default function StorePage() {
  const [activeCat, setActiveCat] = useState('all');
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [checkoutDone, setCheckoutDone] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const [buyErr, setBuyErr] = useState(null);

  const handleBuyNow = async (product) => {
    setBuyErr(null);
    setBuyingId(product.product_id);
    try {
      const { url } = await createPaymentCheckout({
        product_id: product.product_id,
        origin_url: window.location.origin,
      });
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url;
    } catch (e) {
      setBuyErr(e?.response?.data?.detail || e.message || 'Checkout failed');
      setBuyingId(null);
    }
  };

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchCart().then(setCart).catch(() => {});
  }, []);

  const filtered = activeCat === 'all' ? products : products.filter(p => p.category === activeCat);
  const featured = products.filter(p => p.featured);

  const handleAddToCart = async (product) => {
    try {
      const updated = await apiAddToCart(product.product_id, 1);
      setCart(updated);
    } catch (err) {
      console.error('Failed to add to cart', err);
    }
  };

  const handleRemoveFromCart = async (cartItemId) => {
    try {
      await removeFromCart(cartItemId);
      setCart(cart.filter(c => c.cart_item_id !== cartItemId));
    } catch (err) {
      console.error('Failed to remove from cart', err);
    }
  };

  const handleCheckout = async () => {
    try {
      await checkoutCart();
      setCart([]);
      setCheckoutDone(true);
      setTimeout(() => { setCheckoutDone(false); setShowCart(false); }, 3000);
    } catch (err) {
      console.error('Checkout failed', err);
    }
  };

  const cartTotal = cart.reduce((sum, c) => sum + (c.product_price || 0) * (c.quantity || 1), 0);

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto', padding: '16px 24px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, letterSpacing: -0.5, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Store</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
            <Receipt size={20} color={COLORS.textPrimary} />
          </button>
          <button onClick={() => setShowCart(true)} style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', position: 'relative' }}>
            <ShoppingCart size={24} color={COLORS.textPrimary} />
            {cart.length > 0 && (
              <div style={{ position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: COLORS.error, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#FFF' }}>{cart.length}</span>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Category Bar */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '8px 0 16px', marginBottom: 8 }}>
        <button onClick={() => setActiveCat('all')} style={{ padding: '8px 14px', borderRadius: 9999, backgroundColor: activeCat === 'all' ? COLORS.primary : COLORS.surface, border: `1px solid ${activeCat === 'all' ? COLORS.primary : COLORS.border}`, color: activeCat === 'all' ? '#FFF' : COLORS.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          All
        </button>
        {STORE_CATEGORIES.map(c => (
          <button key={c.slug} onClick={() => setActiveCat(c.slug)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9999, backgroundColor: activeCat === c.slug ? COLORS.primary : COLORS.surface, border: `1px solid ${activeCat === c.slug ? COLORS.primary : COLORS.border}`, color: activeCat === c.slug ? '#FFF' : COLORS.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {c.name}
          </button>
        ))}
      </div>

      {/* Featured */}
      {activeCat === 'all' && featured.length > 0 && (
        <>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 14, fontFamily: 'Outfit, sans-serif' }}>Featured Products</h2>
          <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 12, marginBottom: 16 }}>
            {featured.map(p => {
              const Icon = ICON_MAP[p.icon] || Activity;
              return (
                <div key={p.product_id} onClick={() => setSelectedProduct(p)} style={{ minWidth: 240, backgroundColor: COLORS.surface, borderRadius: 24, padding: 16, cursor: 'pointer', flexShrink: 0 }} className="store-card">
                  <div style={{ width: '100%', height: 80, borderRadius: 16, backgroundColor: `${catColor(p.category)}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                    <Icon size={32} color={catColor(p.category)} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{p.name}</div>
                  {p.is_affiliate && <div style={{ marginTop: 4 }}><AffiliateBadge partner={p.partner} /></div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary, fontFamily: 'Outfit, sans-serif' }}>{fmtPrice(p.price) || 'See price'}</span>
                    {p.is_affiliate ? (
                      <button onClick={e => { e.stopPropagation(); window.open(p.affiliate_url, '_blank', 'noopener'); }} style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#B36B00', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
                        <ExternalLink size={16} color="#FFF" />
                      </button>
                    ) : (
                      <button onClick={e => { e.stopPropagation(); handleAddToCart(p); }} style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
                        <ShoppingCart size={16} color="#FFF" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* All Products */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 14, fontFamily: 'Outfit, sans-serif' }}>{activeCat === 'all' ? 'All Products' : STORE_CATEGORIES.find(c => c.slug === activeCat)?.name || 'Products'}</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {filtered.map(p => {
          const Icon = ICON_MAP[p.icon] || Activity;
          return (
            <div key={p.product_id} onClick={() => setSelectedProduct(p)} style={{ flex: '1 1 calc(50% - 6px)', minWidth: 280, display: 'flex', backgroundColor: COLORS.surface, borderRadius: 16, padding: 14, gap: 14, cursor: 'pointer' }} className="store-card">
              <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: `${catColor(p.category)}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={28} color={catColor(p.category)} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{p.name}</span>
                  {p.is_affiliate && <AffiliateBadge partner={p.partner} />}
                </div>
                <p style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.4, marginTop: 4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 17, fontWeight: 800, color: COLORS.primary, fontFamily: 'Outfit, sans-serif' }}>{fmtPrice(p.price) || 'See price'}</span>
                  {p.is_affiliate ? (
                    <button onClick={e => { e.stopPropagation(); window.open(p.affiliate_url, '_blank', 'noopener'); }} style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#B36B00', padding: '8px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer' }}>
                      <ExternalLink size={14} color="#FFF" />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#FFF' }}>{p.partner === 'amazon' ? 'View on Amazon' : 'View'}</span>
                    </button>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); handleAddToCart(p); }} style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: COLORS.primary, padding: '8px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer' }}>
                      <ShoppingCart size={14} color="#FFF" />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#FFF' }}>Add</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Affiliate disclosure */}
      {products.some(p => p.is_affiliate) && (
        <p data-testid="affiliate-disclosure" style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 24, lineHeight: 1.5 }}>
          Some products above are affiliate links from partners such as Amazon. Purchases are
          completed on the partner's site at no extra cost to you. As an Amazon Associate,
          ThriveLonger.ai earns from qualifying purchases.
        </p>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setSelectedProduct(null)}>
          <div style={{ backgroundColor: COLORS.background, borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '90vh', width: '100%', maxWidth: 500, overflowY: 'auto', padding: '16px 24px 20px' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: COLORS.border, margin: '0 auto 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, backgroundColor: `${catColor(selectedProduct.category)}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(() => { const Icon = ICON_MAP[selectedProduct.icon] || Activity; return <Icon size={36} color={catColor(selectedProduct.category)} />; })()}
              </div>
              <button onClick={() => setSelectedProduct(null)} style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
                <X size={22} color={COLORS.textPrimary} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>{selectedProduct.name}</h2>
              {selectedProduct.is_affiliate && <AffiliateBadge partner={selectedProduct.partner} />}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: COLORS.primary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>{fmtPrice(selectedProduct.price) || 'See price on partner site'}</div>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>{selectedProduct.description}</p>
            {selectedProduct.rating != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 2 }}>
                  {[1,2,3,4,5].map(s => <Star key={s} size={18} color="#FFB300" fill={s <= Math.round(selectedProduct.rating) ? '#FFB300' : 'none'} />)}
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{selectedProduct.rating}</span>
                <span style={{ fontSize: 13, color: COLORS.textSecondary }}>({selectedProduct.review_count || selectedProduct.reviewCount || 0} reviews)</span>
              </div>
            )}
            {selectedProduct.is_affiliate ? (
              <>
                <button data-testid={`affiliate-view-${selectedProduct.product_id}`} onClick={() => window.open(selectedProduct.affiliate_url, '_blank', 'noopener')} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#B36B00', padding: 16, borderRadius: 9999, border: 'none', cursor: 'pointer', marginBottom: 8 }}>
                  <ExternalLink size={18} color="#FFF" />
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#FFF' }}>{selectedProduct.partner === 'amazon' ? 'View on Amazon' : 'View on Partner Site'}</span>
                </button>
                <p style={{ fontSize: 11, color: COLORS.textSecondary, textAlign: 'center', lineHeight: 1.5 }}>
                  Purchases are completed on the partner's site. As an Amazon Associate,
                  ThriveLonger.ai earns from qualifying purchases.
                </p>
              </>
            ) : (
              <>
                <button data-testid={`buy-now-${selectedProduct.product_id}`} onClick={() => handleBuyNow(selectedProduct)} disabled={buyingId === selectedProduct.product_id} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: COLORS.primary, padding: 16, borderRadius: 9999, border: 'none', cursor: buyingId === selectedProduct.product_id ? 'wait' : 'pointer', opacity: buyingId === selectedProduct.product_id ? 0.75 : 1, marginBottom: 8 }}>
                  {buyingId === selectedProduct.product_id ? <Loader2 size={18} color="#FFF" style={{ animation: 'spin 1s linear infinite' }} /> : <CreditCard size={18} color="#FFF" />}
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#FFF' }}>{buyingId === selectedProduct.product_id ? 'Redirecting to Stripe…' : `Buy Now — ${fmtPrice(selectedProduct.price)}`}</span>
                </button>
                {buyErr && <div style={{ color: COLORS.error, fontSize: 13, marginBottom: 8, textAlign: 'center' }}>{buyErr}</div>}
                <button onClick={() => { handleAddToCart(selectedProduct); setSelectedProduct(null); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: COLORS.surface, padding: 14, borderRadius: 9999, border: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
                  <ShoppingCart size={18} color={COLORS.textPrimary} />
                  <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>Add to Cart</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setShowCart(false)}>
          <div style={{ backgroundColor: COLORS.background, borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '90vh', width: '100%', maxWidth: 500, overflowY: 'auto', padding: '16px 24px 24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: COLORS.border, margin: '0 auto 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>Your Cart</h2>
              <button onClick={() => setShowCart(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} color={COLORS.textPrimary} /></button>
            </div>

            {checkoutDone ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <CheckCircle2 size={48} color={COLORS.success} />
                <h3 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginTop: 12 }}>Order Confirmed!</h3>
                <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 8 }}>Thank you for your purchase.</p>
              </div>
            ) : cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <ShoppingCart size={48} color={COLORS.border} />
                <p style={{ fontSize: 16, color: COLORS.textSecondary, marginTop: 12 }}>Your cart is empty</p>
              </div>
            ) : (
              <>
                {cart.map(item => (
                  <div key={item.cart_item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${COLORS.border}40` }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{item.product_name}</div>
                      <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Qty: {item.quantity} × ${(item.product_price || 0).toFixed(2)}</div>
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>${((item.product_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                    <button onClick={() => handleRemoveFromCart(item.cart_item_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      <X size={18} color={COLORS.error} />
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>Total</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: COLORS.primary, fontFamily: 'Outfit, sans-serif' }}>${cartTotal.toFixed(2)}</span>
                </div>
                <button
                  data-testid="cart-stripe-checkout-btn"
                  onClick={() => {
                    if (cart.length === 1) {
                      handleBuyNow({ product_id: cart[0].product_id, price: cart[0].product_price });
                    } else {
                      setBuyErr('Stripe checkout supports one product at a time. Use "Buy Now" from each product, or use Add to Cart for the legacy mock flow.');
                    }
                  }}
                  style={{ width: '100%', backgroundColor: COLORS.primary, padding: 16, borderRadius: 9999, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <CreditCard size={18} color="#FFF" />
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#FFF' }}>Checkout with Stripe</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
